import { useState, useMemo } from "react";
import { Check, ChevronsUpDown, MapPin } from "lucide-react";
import { cn } from "@/lib/utils";
import { COMUNI_ALL, COMUNE_COORDS } from "../lib/constants";
import { Popover, PopoverTrigger, PopoverContent } from "./ui/popover";
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from "./ui/command";

const LIMIT = 60;
const NEAR_COUNT = 8;

function rank(list, query) {
  const q = query.trim().toLowerCase();
  if (!q) return list.slice(0, LIMIT);
  const starts = [];
  const contains = [];
  for (const name of list) {
    const low = name.toLowerCase();
    if (low.startsWith(q)) starts.push(name);
    else if (low.includes(q)) contains.push(name);
    if (starts.length >= LIMIT) break;
  }
  return [...starts, ...contains].slice(0, LIMIT);
}

function nearest(refComune) {
  const ref = COMUNE_COORDS[refComune];
  if (!ref) return [];
  const [lat, lng] = ref;
  const scored = [];
  for (const name of COMUNI_ALL) {
    if (name === refComune) continue;
    const c = COMUNE_COORDS[name];
    if (!c) continue;
    const d = (c[0] - lat) ** 2 + (c[1] - lng) ** 2;
    scored.push([d, name]);
  }
  scored.sort((a, b) => a[0] - b[0]);
  return [refComune, ...scored.slice(0, NEAR_COUNT).map((s) => s[1])];
}

export default function ComuneCombobox({
  value,
  onChange,
  testId,
  placeholder = "Cerca comune…",
  allowAll = false,
  allLabel = "Tutti i comuni",
  allValue = "all",
  allowAltro = false,
  nearComune = "",
  className,
  triggerClassName,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  const results = useMemo(() => rank(COMUNI_ALL, query), [query]);
  const nearList = useMemo(() => nearComune ? nearest(nearComune) : [], [nearComune]);

  const label = value && value !== allValue ? value : (allowAll ? allLabel : "");
  const q = query.trim().toLowerCase();
  const showAltro = allowAltro && (!q || "altro".includes(q));
  const showAll = allowAll && (!q || allLabel.toLowerCase().includes(q));
  const showNear = !q && nearList.length > 0;

  const select = (v) => { onChange(v); setOpen(false); setQuery(""); };

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) setQuery(""); }}>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          data-testid={testId}
          className={cn(
            "flex h-11 w-full items-center justify-between whitespace-nowrap rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm ring-offset-background focus:outline-none focus:ring-1 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50",
            triggerClassName,
            className,
          )}
        >
          <span className={cn("line-clamp-1 text-left", !label && "text-muted-foreground")}>{label || placeholder}</span>
          <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="p-0 z-[1200] w-[--radix-popover-trigger-width]" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            value={query}
            onValueChange={setQuery}
            placeholder={placeholder}
            data-testid={testId ? `${testId}-input` : undefined}
          />
          <CommandList className="max-h-64">
            {results.length === 0 && !showAll && !showAltro && (
              <CommandEmpty>Nessun comune trovato.</CommandEmpty>
            )}
            {showAll && (
              <CommandGroup>
                <CommandItem value={allValue} onSelect={() => select(allValue)} data-testid={testId ? `${testId}-opt-all` : undefined}>
                  <Check className={cn("mr-2 h-4 w-4", value === allValue ? "opacity-100" : "opacity-0")} />
                  {allLabel}
                </CommandItem>
              </CommandGroup>
            )}
            {showNear && (
              <CommandGroup heading="Vicino a te">
                {nearList.map((c) => (
                  <CommandItem key={`near-${c}`} value={`near-${c}`} onSelect={() => select(c)}>
                    <MapPin className={cn("mr-2 h-4 w-4", value === c ? "text-primary" : "text-muted-foreground")} />
                    {c}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
            <CommandGroup heading={showNear ? "Tutti i comuni" : undefined}>
              {results.map((c) => (
                <CommandItem key={c} value={c} onSelect={() => select(c)}>
                  <Check className={cn("mr-2 h-4 w-4", value === c ? "opacity-100" : "opacity-0")} />
                  {c}
                </CommandItem>
              ))}
              {showAltro && (
                <CommandItem value="__altro__" onSelect={() => select("Altro")}>
                  <Check className={cn("mr-2 h-4 w-4", value === "Altro" ? "opacity-100" : "opacity-0")} />
                  Altro
                </CommandItem>
              )}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
